import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Industries from '../components/Industries';
import CTA from '../components/CTA';
import OurCommitment from '../components/OurCommitment';
import { generateCTAMessage } from '../utils/ctaMessages.js';

const IndustriesOverviewPage = () => {
  const [scrollProgress, setScrollProgress] = useState(0);

  // Scroll progress tracking
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = (scrollTop / docHeight) * 100;
      setScrollProgress(scrollPercent);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      {/* Scroll Progress Indicator */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: `${scrollProgress}%`,
        height: '3px',
        background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
        zIndex: 9999,
        transition: 'width 0.1s ease-out'
      }}></div>

      {/* Breadcrumb Navigation */}
      <div style={{
        position: 'fixed',
        top: '65px',
        left: 0,
        right: 0,
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(14, 165, 233, 0.1)',
        padding: '1rem 0',
        zIndex: 999,
        transition: 'all 0.3s ease'
      }}>
        <div className="container">
          <nav style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Link to="/" style={{
              color: '#64748b',
              textDecoration: 'none',
              fontSize: '0.9rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem',
              transition: 'color 0.3s ease'
            }}
            onMouseEnter={(e) => e.target.style.color = '#0ea5e9'}
            onMouseLeave={(e) => e.target.style.color = '#64748b'}>
              <i className="bi bi-house"></i>
              <span>Home</span>
            </Link>
            <i className="bi bi-chevron-right" style={{ color: '#cbd5e1', fontSize: '0.8rem' }}></i>
            <span style={{
              color: '#0ea5e9',
              fontSize: '0.9rem',
              fontWeight: '600'
            }}>
              Industries
            </span>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section id="hero" className="industries-hero-section" style={{
                    paddingTop: '130px' // Account for fixed header + breadcrumb height
      }}>
        <div className="hero-background" style={{ 
          background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #333333 100%)',
          position: 'relative',
          overflow: 'hidden',
          minHeight: '80vh',
          display: 'flex',
          alignItems: 'center',
          paddingBottom: '4rem'
        }}>
          {/* Background Pattern */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              radial-gradient(circle at 20% 80%, rgba(14, 165, 233, 0.06) 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, rgba(56, 189, 248, 0.04) 0%, transparent 50%)
            `,
            zIndex: 1
          }}></div>
          
          {/* Floating Particles */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 2,
            pointerEvents: 'none'
          }}>
            {[...Array(8)].map((_, i) => (
              <div key={i} style={{
                position: 'absolute',
                width: '6px',
                height: '6px',
                background: 'rgba(56, 189, 248, 0.4)',
                borderRadius: '50%',
                top: `${15 + i * 12}%`,
                left: `${8 + i * 15}%`,
                animation: `float ${4 + i * 0.3}s ease-in-out infinite`,
                animationDelay: `${i * 0.4}s`
              }}></div>
            ))}
          </div>

          <div className="container" style={{ position: 'relative', zIndex: 3 }}>
            <div className="row justify-content-center text-center">
              <div className="col-lg-10">
                <div className="hero-content">
                  {/* Industry Icon */}
                  <div className="industry-icon-large mb-4" style={{
                    display: 'inline-block',
                    background: 'rgba(30, 41, 59, 0.8)',
                    border: '2px solid rgba(14, 165, 233, 0.3)',
                    borderRadius: '50%',
                    padding: '2rem',
                    backdropFilter: 'blur(15px)',
                    boxShadow: '0 8px 25px rgba(14, 165, 233, 0.2)'
                  }}>
                    <span style={{ fontSize: '4rem' }}>🏢</span>
                  </div>
                  
                  {/* Title and Subtitle */}
                  <h1 className="hero-title mb-3" style={{
                    fontSize: 'clamp(2rem, 4vw, 3rem)',
                    fontWeight: '800',
                    color: '#ffffff',
                    textShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
                    lineHeight: '1.2',
                    marginBottom: '2rem'
                  }}>Industries We Serve</h1>
                  
                  <h2 className="hero-subtitle mb-4" style={{
                    fontSize: 'clamp(1.2rem, 2.5vw, 1.5rem)',
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontWeight: '400',
                    lineHeight: '1.4',
                    marginBottom: '2rem'
                  }}>Specialized AI solutions with tangible use-cases across diverse sectors</h2>
                  
                  {/* Overview */}
                  <p className="hero-description mb-5" style={{
                    fontSize: '1.1rem',
                    color: 'rgba(255, 255, 255, 0.8)',
                    maxWidth: '800px',
                    margin: '0 auto 3rem',
                    lineHeight: '1.6'
                  }}>From healthcare to cybersecurity, we deliver proven AI solutions that drive real business value. Explore our industry-specific expertise and see how we can transform your sector.</p>
                  
                  {/* CTA Buttons */}
                  <div className="hero-cta" style={{
                    display: 'flex',
                    gap: '1.5rem',
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                    marginBottom: '2rem'
                  }}>
                    <Link to="/contact" style={{
                      background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                      border: 'none',
                      color: '#ffffff',
                      padding: '1.4rem 3.5rem',
                      borderRadius: '60px',
                      fontWeight: '700',
                      fontSize: '1.1rem',
                      textDecoration: 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: '0 10px 30px rgba(14, 165, 233, 0.4), 0 6px 20px rgba(0, 0, 0, 0.1)',
                      minWidth: '220px',
                      justifyContent: 'center',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-5px)';
                      e.target.style.boxShadow = '0 15px 40px rgba(14, 165, 233, 0.5), 0 10px 30px rgba(0, 0, 0, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 10px 30px rgba(14, 165, 233, 0.4), 0 6px 20px rgba(0, 0, 0, 0.1)';
                    }}>
                      <i className="bi bi-rocket-takeoff"></i>
                      Start Your Project
                    </Link>
                    
                    <Link to="/industries/comparison" style={{
                      background: 'rgba(255, 255, 255, 0.08)',
                      border: '2px solid rgba(14, 165, 233, 0.3)',
                      color: '#ffffff',
                      padding: '1.4rem 3.5rem',
                      borderRadius: '60px',
                      fontWeight: '700',
                      fontSize: '1.1rem',
                      textDecoration: 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      backdropFilter: 'blur(20px)',
                      minWidth: '220px',
                      justifyContent: 'center',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-5px)';
                      e.target.style.background = 'rgba(255, 255, 255, 0.15)';
                      e.target.style.borderColor = 'rgba(14, 165, 233, 0.6)';
                      e.target.style.boxShadow = '0 15px 40px rgba(14, 165, 233, 0.2), 0 10px 30px rgba(0, 0, 0, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.background = 'rgba(255, 255, 255, 0.08)';
                      e.target.style.borderColor = 'rgba(14, 165, 233, 0.3)';
                      e.target.style.boxShadow = 'none';
                    }}>
                      <i className="bi bi-graph-up-arrow"></i>
                      Compare Industries
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <Industries />

      {/* Our Commitment Section */}
      <div id="commitment">
        <OurCommitment />
      </div>

      {/* CTA Section */}
      <div id="cta">
        <CTA {...generateCTAMessage('industries-overview')} />
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }
      `}</style>
    </>
  );
};

export default IndustriesOverviewPage;
